/**
 * Servicio para colas de turnos y sus entradas (gestor de llamamientos)
 *
 * Sustituye al antiguo src/services/colas.service.js y a los modelos
 * Cola/EntradaCola, retirados al migrar la feature: devuelve los datos del
 * backend tal cual, sin capa de modelos.
 *
 * Contrato: api/docs/api/colas.md. Todas las rutas de /colas y /entradas-cola
 * responden envueltas en { data, message } y se desenvuelven aquí; ambas exigen
 * token y permiso can.colas.* / can.entradasCola.*.
 *
 * Forma de una cola:
 *   { id, nombre, creado, modificado, entradas: Entrada[] }
 * Forma de una entrada:
 *   { id, numero, posicion, estado, mostrador, id_cola, creado, modificado, detalles }
 *   ATENCIÓN: id_cola llega en snake_case (el DTO no lo convierte); se deja tal cual.
 */

import axios from "axios";
import HostService from "../../../services/host.service";
import AuthService from "../../../services/auth.service";

/**
 * Relanza un error de Axios como Error con mensaje legible y código HTTP.
 * @param {Object} error - Error capturado de Axios
 * @param {string} fallback - Mensaje genérico propio del método
 * @param {string} contexto - Descripción para el console.error
 * @throws {Error} Error con .message y .status (undefined si no hubo respuesta)
 */
const relanzar = (error, fallback, contexto) => {
    console.error(contexto, error);
    const data = error.response?.data;
    // DECISION: en un 400 del validator el message es genérico ("Parametros incorrectos")
    // y el motivo real viene en details[0].msg; en el resto details es un objeto
    const detalleValidacion = Array.isArray(data?.details) ? data.details[0]?.msg : undefined;
    const wrapped = new Error(detalleValidacion || data?.message || data?.error?.message || error.message || fallback);
    wrapped.status = error.response?.status;
    throw wrapped;
};

/**
 * Construye la URL absoluta a partir de una ruta relativa de la API
 * @param {string} ruta - Ruta sin barra inicial
 * @returns {string} URL completa
 */
const url = (ruta) => HostService.getHost() + ruta;

/**
 * Servicio para gestión de colas de turnos
 */
class ColasService {
    /**
     * GET colas - Lista todas las colas
     * @returns {Promise<Array>} Colas (con `entradas` siempre []); [] si no llega data
     * @throws {Error} Si data no es un array o falla la petición
     */
    static async list() {
        try {
            const res = await axios.get(url('colas'), AuthService.setToken());
            const colas = res.data?.data;
            if (!colas) return [];
            if (!Array.isArray(colas)) {
                throw new Error('La API devolvió un formato inesperado al listar las colas');
            }
            return colas;
        } catch (error) {
            return relanzar(error, 'Error al listar las colas', 'Error al listar colas:');
        }
    }

    /**
     * GET colas/:id - Obtiene una cola con sus entradas del día, ordenadas por posicion
     * @param {number|string} id - Id de la cola
     * @returns {Promise<Object>} Cola con 'entradas'
     * @throws {Error} Con .status 400 si el id no es válido, 404 si no existe
     */
    static async getById(id) {
        try {
            const res = await axios.get(url(`colas/${id}`), AuthService.setToken());
            return res.data.data;
        } catch (error) {
            return relanzar(error, 'Error al obtener la cola', 'Error al obtener cola:');
        }
    }

    /**
     * POST colas - Crea una cola
     * @param {Object} datos - Datos de la cola
     * @param {string} datos.nombre - Nombre obligatorio, máx. 255 caracteres (el backend lo recorta)
     * @returns {Promise<Object>} Cola creada (201), con el id asignado
     * @throws {Error} Con .status 400 si el nombre no es válido
     */
    static async create({ nombre }) {
        try {
            const res = await axios.post(url('colas'), { nombre }, AuthService.setToken());
            return res.data.data;
        } catch (error) {
            return relanzar(error, 'Error al crear la cola', 'Error al crear cola:');
        }
    }

    /**
     * PUT colas/:id - Actualiza el nombre de una cola
     * @param {number|string} id - Id de la cola
     * @param {Object} datos - Datos a modificar
     * @param {string} datos.nombre - Nuevo nombre, obligatorio (mismas reglas que create)
     * @returns {Promise<Object>} Cola actualizada
     * @throws {Error} Con .status 400 si el nombre no es válido, 404 si no existe
     */
    static async update(id, { nombre }) {
        try {
            const res = await axios.put(url(`colas/${id}`), { nombre }, AuthService.setToken());
            return res.data.data;
        } catch (error) {
            return relanzar(error, 'Error al modificar la cola', 'Error al modificar cola:');
        }
    }

    /**
     * DELETE colas/:id - Elimina una cola
     * @param {number|string} id - Id de la cola
     * @returns {Promise<Object>} Cola eliminada (datos previos al borrado)
     * @throws {Error} Con .status 404 si no existe, 409 si tiene entradas asociadas
     */
    static async delete(id) {
        try {
            const res = await axios.delete(url(`colas/${id}`), AuthService.setToken());
            return res.data.data;
        } catch (error) {
            return relanzar(error, 'Error al eliminar la cola', 'Error al eliminar cola:');
        }
    }

    /**
     * POST entradas-cola/:idCola/siguiente - Llama a la siguiente entrada en espera
     * @param {number|string} idCola - Id de la cola
     * @param {string} mostrador - Mostrador que llama
     * @returns {Promise<Object>} Entrada llamada
     * @throws {Error} Con .status 404 (NO_HAY_EN_ESPERA, 'No hay más en espera') si no
     *   queda nadie (la UI lo muestra como aviso, no como error)
     */
    static async llamarSiguiente(idCola, mostrador) {
        try {
            const res = await axios.post(url(`entradas-cola/${idCola}/siguiente`), { mostrador }, AuthService.setToken());
            return res.data.data;
        } catch (error) {
            return relanzar(error, 'Error al llamar al siguiente', 'Error al llamar al siguiente:');
        }
    }

    /**
     * POST entradas-cola/:idEntrada/llamar - Llama a una entrada concreta
     * @param {number|string} idEntrada - Id de la entrada
     * @param {string} mostrador - Mostrador que llama
     * @returns {Promise<Object>} Entrada actualizada
     * @throws {Error} Con .status 404 si la entrada no existe
     */
    static async llamar(idEntrada, mostrador) {
        try {
            const res = await axios.post(url(`entradas-cola/${idEntrada}/llamar`), { mostrador }, AuthService.setToken());
            return res.data.data;
        } catch (error) {
            return relanzar(error, 'Error al llamar la entrada', 'Error al llamar entrada:');
        }
    }

    /**
     * POST entradas-cola/:idEntrada/finalizar - Finaliza una entrada
     * DECISION: la config de cabeceras va como tercer argumento y el body es null;
     * el legacy la pasaba como body y no enviaba Authorization.
     * @param {number|string} idEntrada - Id de la entrada
     * @returns {Promise<Object>} Entrada actualizada
     */
    static async finalizar(idEntrada) {
        try {
            const res = await axios.post(url(`entradas-cola/${idEntrada}/finalizar`), null, AuthService.setToken());
            return res.data.data;
        } catch (error) {
            return relanzar(error, 'Error al finalizar la entrada', 'Error al finalizar entrada:');
        }
    }

    /**
     * POST entradas-cola/:idEntrada/saltar - Salta una entrada
     * (misma decisión de cabeceras que finalizar)
     * @param {number|string} idEntrada - Id de la entrada
     * @returns {Promise<Object>} Entrada actualizada
     */
    static async saltar(idEntrada) {
        try {
            const res = await axios.post(url(`entradas-cola/${idEntrada}/saltar`), null, AuthService.setToken());
            return res.data.data;
        } catch (error) {
            return relanzar(error, 'Error al saltar la entrada', 'Error al saltar entrada:');
        }
    }

    /**
     * POST entradas-cola - Registra un turno (alta de una entrada en la cola, 201)
     * El input del backend va en snake_case (id_cola, pedidos_cliente). Con numero
     * null o vacío el backend genera un turno sin pedido 'SP-XXXX'. estado y posicion
     * los fija el servidor (los ignora si llegan en el body).
     * @param {Object} entrada - Datos del turno
     * @param {string|null} entrada.numero - Pedido 'serie-numero', o null/'' si no tiene pedido
     * @param {number|string} entrada.id_cola - Id de la cola
     * @param {number|string} entrada.ejercicio - Ejercicio (año) del pedido
     * @param {boolean} [entrada.pedidos_cliente] - true para incluir todos los pedidos del cliente
     * @returns {Promise<Object>} Entrada registrada: { id, numero, posicion, estado,
     *   mostrador, id_cola, creado, modificado, detalles }
     * @throws {Error} Con .status y el .message del backend:
     *   400 FORMATO_PEDIDO_INVALIDO / EJERCICIO_INVALIDO, 404 PEDIDO_NO_ENCONTRADO,
     *   404 PEDIDO_RECOGIDO, 409 TURNO_DUPLICADO, 409 PEDIDO_DUPLICADO,
     *   422 PEDIDO_NO_MOSTRADOR; .status undefined si no hubo respuesta
     */
    static async registrar(entrada) {
        try {
            const res = await axios.post(url('entradas-cola'), entrada, AuthService.setToken());
            return res.data.data;
        } catch (error) {
            return relanzar(error, 'Error al registrar el turno', 'Error al registrar turno:');
        }
    }

    /**
     * GET entradas-cola/:idEntrada/albaranes - Albaranes asociados a una entrada
     * @param {number|string} idEntrada - Id de la entrada
     * @returns {Promise<Array<string>>} Albaranes (strings 'SERIE-NUMERO', se parsean con
     *   Utils.getOrder); [] si la entrada no existe o no tiene pedidos
     */
    static async getAlbaranes(idEntrada) {
        try {
            const res = await axios.get(url(`entradas-cola/${idEntrada}/albaranes`), AuthService.setToken());
            return res.data.data;
        } catch (error) {
            return relanzar(error, 'Error al obtener los albaranes', 'Error al obtener albaranes:');
        }
    }
}

export default ColasService;
